import { transposeChordToken } from './transposeChords';

// Natural open-chord semitone positions (CAGED shapes: C=0, D=2, E=4, G=7, A=9)
const CAGED_SEMITONES = new Set([0, 2, 4, 7, 9]);

const CAGED_SHAPE: Record<number, 'C' | 'D' | 'E' | 'G' | 'A'> = {
  0: 'C',
  2: 'D',
  4: 'E',
  7: 'G',
  9: 'A',
};

const KEY_TO_SEMITONE: Record<string, number> = {
  C: 0, 'B#': 0,
  'C#': 1, Db: 1,
  D: 2,
  'D#': 3, Eb: 3,
  E: 4, Fb: 4,
  F: 5, 'E#': 5,
  'F#': 6, Gb: 6,
  G: 7,
  'G#': 8, Ab: 8,
  A: 9,
  'A#': 10, Bb: 10,
  B: 11, Cb: 11,
};

// ── Bass simplification ───────────────────────────────────────────────────────

/**
 * Simplify a chord for bass view: keeps root + accidental + 'm' if minor.
 * All quality suffixes (7, maj7, sus, dim, aug, add, etc.) and slash bass
 * notes are stripped.
 *
/**
 * Simplify chord for bass view:
 * - If chord has inversion (slash), show the bass note (after the slash)
 * - Otherwise, show root + optional 'm'
 * Examples:
 *   A/C#   → C#  (bass note of the inversion)
 *   D/F#   → F#  (bass note of the inversion)
 *   Am/G   → G   (bass note of the inversion)
 *   Am     → Am  (no inversion, keep root + m)
 *   Cmaj7  → C   (no inversion, simplify to root)
 *   F#m7   → F#m (no inversion, keep root + m)
 *   Bbsus4 → Bb  (no inversion, keep root)
 *   Bdim   → B   (no inversion, keep root)
 */
export function simplifyForBass(chord: string): string {
  // Check if chord has a slash (inversion)
  const slashIndex = chord.indexOf('/');
  if (slashIndex !== -1) {
    // Extract bass note (everything after the slash)
    const bassNote = chord.slice(slashIndex + 1);
    return bassNote;
  }

  // No inversion — show root + optional 'm'
  const rootMatch = chord.match(/^([A-G][b#]?)/);
  if (!rootMatch) return chord;

  const root = rootMatch[1];
  const rest = chord.slice(root.length);

  // Minor: rest starts with 'm' but NOT 'maj'
  const isMinor = rest.startsWith('m') && !rest.startsWith('maj');
  return root + (isMinor ? 'm' : '');
}

// ── Capo transposition ────────────────────────────────────────────────────────

/**
 * Return the chord shape a guitarist reads with a capo at capoFret.
 * Transposes down by capoFret semitones with sharp preference.
 *
 * @throws {RangeError} if capoFret is not 1–12
 */
export function transposeChordForCapo(chord: string, capoFret: number): string {
  if (capoFret < 1 || capoFret > 12) {
    throw new RangeError(`capoFret must be between 1 and 12, got ${capoFret}`);
  }
  return transposeChordToken(chord, -capoFret);
}

// ── Capo compatibility helpers ────────────────────────────────────────────────

export interface CapoSuggestion {
  fret: number;           // 0 = no capo (Standard mode)
  shape: 'C' | 'D' | 'E' | 'G' | 'A' | null; // null when fret=0
  delta: number;          // absolute distance from currentCapoFret
}

/**
 * Check whether the current capo fret yields a natural CAGED shape for the
 * given key semitone value.
 */
export function isCapoCompatible(keySemitone: number, capoFret: number): boolean {
  const shapeSemitone = ((keySemitone - capoFret) % 12 + 12) % 12;
  return CAGED_SEMITONES.has(shapeSemitone);
}

/**
 * Return up to 3 capo suggestions sorted by smallest fret delta from
 * currentCapoFret. Fret 0 = "remove capo" (switch to Standard mode).
 *
 * Only frets 0–7 are considered (higher frets are impractical in worship).
 */
export function getCapoSuggestions(
  keySemitone: number,
  currentCapoFret: number,
): CapoSuggestion[] {
  const suggestions: CapoSuggestion[] = [];

  for (let fret = 0; fret <= 7; fret++) {
    if (fret === currentCapoFret) continue; // skip current (it's already incompatible)
    const shapeSemitone = ((keySemitone - fret) % 12 + 12) % 12;
    if (fret === 0 || CAGED_SEMITONES.has(shapeSemitone)) {
      suggestions.push({
        fret,
        shape: fret === 0 ? null : CAGED_SHAPE[shapeSemitone],
        delta: Math.abs(fret - currentCapoFret),
      });
    }
  }

  suggestions.sort((a, b) => a.delta - b.delta);
  return suggestions.slice(0, 3);
}

// ── View mode application ─────────────────────────────────────────────────────

export type ViewMode = 'STANDARD' | 'BASS' | 'CAPO';

/**
 * Apply the participant's view mode to a single chord token.
 *
 * - STANDARD: returns chord unchanged
 * - BASS:     simplifies to root + optional 'm'
 * - CAPO:     transposes down by capoFret semitones
 */
export function applyViewMode(
  chord: string,
  viewMode: ViewMode,
  capoFret?: number,
): string {
  switch (viewMode) {
    case 'BASS':
      return simplifyForBass(chord);
    case 'CAPO':
      if (capoFret == null) return chord;
      return transposeChordForCapo(chord, capoFret);
    case 'STANDARD':
    default:
      return chord;
  }
}
